//src/Components/Buttons/MultiFileUploader/index.tsx
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import {
  AttachmentErrors,
  IAttachment,
} from 'app/features/conversations/Components/MessageEditor/Uploader';
import DocumentPill from 'app/features/conversations/Components/MessageEditor/Uploader/DocumentPill';
import useParallelQueries from 'app/hooks/useParallelQueries';
import { IDocument } from 'app/services/documents';
import {
  AttachIcon,
  CancelIcon,
  CheckedIcon,
  DeleteDarkIcon,
  FileIcon,
  UploadIcon,
  WarningIcon,
} from 'assets/icons';
import ErrorMessageStyle from 'Components/Form/ErrorMessageStyle';
import Spinner from 'Components/Spinner';
import Tooltip from 'Components/Tooltips';
import { uniqueId } from 'lodash';
import { forwardRef, Ref, useImperativeHandle, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import * as Yup from 'yup';
import Button from '../Button';
import useAttachments from './useAttachments';

interface MultiFileUploaderProps {
  maxFilesCount?: number;
  maxFileSize?: number;
  uploadDocumentHandle?: (attachment: IAttachment) => () => Promise<IDocument>;
}

// ref : uplaodAll
export interface MultiFileUploaderRef {
  upload: (
    promiseFn: (attachment: IAttachment) => () => Promise<IDocument>,
  ) => Promise<IDocument[]>;
}

// TODO make compatible with react hook form
const MultiFileUploader = forwardRef(
  (
    { maxFilesCount, maxFileSize }: MultiFileUploaderProps,
    ref: Ref<MultiFileUploaderRef>,
  ) => {
    const [attachmentErrors, setAttachmentErrors] =
      useState<AttachmentErrors>();
    const [state, setState] = useState<'staging' | 'uploading'>('staging');

    const {
      fetchAll,
      results,
      unregister: unregisterParallelQueryHandle,
      unregiserAll,
      registerEndpoints,
      createEndpoint,
    } = useParallelQueries<IDocument>();

    const {
      attachments,
      addAttachements,
      deleteAttachment,
      errors,
      deleteAllAttachments,
    } = useAttachments({
      maxFilesCount,
      maxFileSize,
    });

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop: addAttachements,
    });

    useImperativeHandle(ref, () => ({
      upload: (
        promiseFn: (attachment: IAttachment) => () => Promise<IDocument>,
      ) => {
        const endpoints = (attachments || []).map((a) =>
          createEndpoint(a.id, promiseFn(a)),
        );
        registerEndpoints(endpoints);
        return fetchAll(endpoints).then((results) => {
          return results.endpoints
            .filter((_) => _.status === 'fulfilled' && _.data)
            .map((_) => _.data!);
        });
      },
    }));

    return (
      <div
        {...getRootProps()}
        style={{ minHeight: '48px' }}
        className={`  rounded-xl cursor-pointer max-h-44 overflow-y-auto flex gap-x-2 text-sm items-center gap-y-1 flex-wrap border-dotted  border ${
          isDragActive
            ? 'border-green focus-within:border-green'
            : 'border-gray-100 focus-within:border-gray-light '
        }`}
      >
        <input {...getInputProps()} />
        {!attachments || attachments.length === 0 ? (
          <div className="flex  py-2 flex-1 justify-center text-gray-700 items-center">
            <UploadIcon className="w-7 h-7 mr-2 " />
            <div>Upload or drop files to attach.</div>
          </div>
        ) : (
          <div className="flex items-center flex-1">
            {
              <>
                <div
                  className={`max-h-20 py-2 overflow-y-auto flex-1 flex text-xs gap-x-2 items-center pl-3 gap-y-1 flex-wrap`}
                >
                  {attachments.map((attachment) => (
                    <DocumentPill
                      key={attachment.id}
                      state={
                        results?.[attachment.id]?.status ||
                        QueryStatus.uninitialized
                      }
                      attachment={attachment}
                      onDelete={(e) => {
                        deleteAttachment(attachment);
                        unregisterParallelQueryHandle(attachment.id);
                      }}
                    />
                  ))}
                </div>

                <div className="flex">
                  <button
                    disabled={!attachments.length}
                    className="ml-2 mr-2 hover:text-gray-dark p-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      deleteAllAttachments();
                      unregiserAll();
                    }}
                  >
                    <CancelIcon className="w-3 h-3" />
                  </button>
                  <div
                    className={`pr-3 self-stretch py-2 ${
                      isDragActive
                        ? 'text-green'
                        : 'text-gray-700 hover:text-green'
                    }`}
                  >
                    <UploadIcon className="w-7 h-7 "></UploadIcon>
                  </div>
                </div>
              </>
            }

            {errors?.maxFilesCount && (
              <div className="pt-2 text-red-dark text-xs">
                {errors?.maxFilesCount}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);
export type DocumentAttachment = {
  file: File;
  title: string;
  doctype: string;
  description: string;
  uuid: string;
  isLoading?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  isUnitialized?: boolean;
};

export const filesValidationSchema = Yup.array().of(
  Yup.object().shape({
    title: Yup.string()
      .trim()
      .required('Document Title is required')
      .matches(/.+\..+$/, 'Document extension is required')
      .max(100, 'Max Document title length is 100 characters'),
    file: Yup.mixed().required('required'),
    // .test('fileSize', 'File is too large', (value) => {
    //   return value && value.size <= MAX_FILE_SIZE;
    // }),
  }),
);

export function MultiFileSelector({
  value: files = [],
  isLoading,
  onChange,
  errors,
  showUpload = true,
}: {
  showUpload?: boolean;
  isLoading?: boolean;
  value: DocumentAttachment[];
  errors:
    | Merge<FieldError, (FieldErrorsImpl<DocumentAttachment> | undefined)[]>
    | undefined;
  onChange: (files: DocumentAttachment[]) => void;
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    maxSize: 5 * 10124 * 1024,

    onDrop: (dropped) => {
      onChange(
        files.concat(
          dropped.map((d) => {
            return {
              file: d,
              // @ts-ignore
              title: d.name,
              doctype: 'Other',
              description: '',
              isUnitialized: true,
              isError: false,
              isLoading: false,
              isSuccess: false,
              uuid: uniqueId(),
            };
          }),
        ),
      );
    },
  });

  return (
    <div
      {...getRootProps()}
      style={{ minHeight: '48px' }}
      className={`rounded-xl h-full overflow-y-auto cursor-pointer focus:shadow flex gap-x-2 text-sm gap-y-1 flex-wrap  border ${
        isDragActive ? 'border-green' : 'border-gray-100'
      }`}
    >
      <input {...getInputProps()} />
      {files.length === 0 && (
        <div className="flex  py-2 flex-1 justify-center text-gray-700 items-center">
          <UploadIcon className="w-7 h-7 mr-2 " />
          <div>Upload or drop files to attach.</div>
        </div>
      )}

      {files.length > 0 && (
        <div className="w-full ">
          <div className="flex justify-between gap-2 p-2 items-center border-b border-gray-100">
            <span className="text-gray-700">{files.length} Files</span>
            <div className="flex gap-2">
              <Button type="button" className="max-w-min">
                <AttachIcon className="w-5 h-5" />
              </Button>

              {showUpload && (
                <Tooltip message="Upload Attached Files">
                  <Button
                    isLoading={isLoading}
                    className="max-w-min"
                    type="submit"
                    onClick={(e) => {
                      // stops the file dialog from opening
                      e.stopPropagation();
                    }}
                  >
                    {!isLoading && <UploadIcon className="w-5 h-5" />}
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          <div
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {files.map((file, index) => (
              <NewDocumentEditor
                key={file.title + index}
                file={file}
                onDelete={() =>
                  onChange(files.filter((f) => f.uuid !== file.uuid))
                }
                onChange={(updated) =>
                  onChange(
                    files.map((f) =>
                      f.uuid === file.uuid ? Object.assign(file, updated) : f,
                    ),
                  )
                }
                error={
                  errors &&
                  errors[index] && (
                    <ErrorMessageStyle
                      message={
                        errors[index]?.file?.message ||
                        errors[index]?.title?.message ||
                        errors[index]?.doctype?.message
                      }
                    />
                  )
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NewDocumentEditor({
  file,
  onDelete,
  error,
  onChange,
}: {
  file: DocumentAttachment;
  onDelete: () => void;
  onChange: (file: DocumentAttachment) => void;
  error: React.ReactNode;
  isLoading?: boolean;
  isSuccess?: boolean;
  hasError?: boolean;
}) {
  const state = file.isLoading
    ? 'loading'
    : file.isSuccess
    ? 'uploaded'
    : file.isError
    ? 'error'
    : 'edit';

  return (
    <>
      <div
        className="border-b h-10 border-gray-100 px-2 cursor-default flex text-gray-dark items-center flex-1  gap-2  font-semibold"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <FileIcon className="w-5 h-5 bg-white" />
        <input
          defaultValue={file.title || ''}
          disabled={state !== 'edit'}
          onKeyDown={(e) => {
            const input = e.target as HTMLInputElement;

            if (e.key === 'Enter') {
              e.stopPropagation();
              e.preventDefault();
              input.blur();
            }
          }}
          onBlur={(_) => onChange({ ...file, title: _.target.value })}
          className="w-full bg-transparent rounded p-1 outline-none focus:outline-offset-0 focus:outline-1 focus:outline-green/50 disabled:bg-transparent focus:bg-gray-100/10 text-gray-dark px-1 hover:bg-gray-100/10 "
        />

        <Button
          tabIndex={-1}
          type="button"
          className=" max-w-[34px] max-h-[3px]"
          disabled={state === 'loading' || state === 'uploaded'}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDelete();
          }}
        >
          {state === 'edit' && (
            <DeleteDarkIcon className="text-red-200 w-4 h-4 " />
          )}

          {state === 'loading' && (
            <Spinner className="w-5 h-5" bounce={false} />
          )}

          {state === 'uploaded' && <CheckedIcon className="w-5 h-5" />}

          {state === 'error' && (
            <Tooltip message="Upload Error">
              <WarningIcon className="w-5 h-5" />
            </Tooltip>
          )}
        </Button>
      </div>

      {error && <div className="px-2">{error}</div>}
    </>
  );
}

export default MultiFileUploader;
